import type { SurfaceField } from './modSurface';

/**
 * modMount.ts (M154) — where a part that STANDS on the body (the keeper's
 * gloves, the trophy topper) may stand: on unbroken paint, clear of every
 * opening the ride's measured surface knows (a hood vent's slots, a cowl
 * vent, a scoop, a grille, a lamp, the windscreen). The anchor table gives
 * each ride one spot per part; on the rebuilt coupe that spot put the gloves
 * on its louvred hood slots. The search keeps the anchor whenever it is
 * already clear, and otherwise takes the NEAREST clear spot on the same
 * panel (never off an edge, never up a windscreen or down a step).
 */

export interface MountQuery {
  /** The anchor (car-local). */
  x: number;
  z: number;
  /** The part's footprint radius. */
  r: number;
  /** M154 r2: the radius it must stand on the panel by (default `r`: a
   *  glove's base is narrower than its thumb's reach). */
  support?: number;
  /** Paint kept clear around the footprint (default 3 cm). */
  ring?: number;
  /** How far the search may move the part, across / along the car. */
  dx?: number;
  dz?: number;
  /** Search step (default 2 cm). */
  step?: number;
  /** How much higher or lower than the anchor's panel a new spot may be. */
  maxRise?: number;
  /** Other parts' footprints to keep clear of (another mod on the hood). */
  avoid?: readonly { x: number; z: number; r: number }[];
  /** M154 r2: a further test a spot must pass (the cup keeps openings off
   *  its sides). */
  accept?: (x: number, z: number) => boolean;
}

export interface Mount {
  x: number;
  z: number;
  /** The clear paint between the footprint and the nearest opening or
   *  neighbour (negative = the footprint overlaps one). */
  clearance: number;
  moved: boolean;
  /** M154 r2: the spot meets the ring (and `accept`): a clean mount. */
  ok: boolean;
}

/** The clear distance from a footprint's edge at (x, z) to the nearest
 *  opening or avoided part. */
function clearanceAt(surf: SurfaceField, q: MountQuery, x: number, z: number, reach: number): number {
  let d = surf.openDistance(x, z, reach);
  for (const o of q.avoid ?? []) d = Math.min(d, Math.hypot(o.x - x, o.z - z) - o.r);
  return d - q.r;
}

/** The footprint stands on the panel: measured all round, no edge falling
 *  away under it (a shoulder, the nose) and no wall rising inside it (a
 *  windscreen, a scoop's flank). */
function supported(surf: SurfaceField, x: number, z: number, r: number, y0: number, maxRise: number): boolean {
  const y = surf.topAt(x, z);
  if (y === null || Math.abs(y - y0) > maxRise) return false;
  for (let k = 0; k < 8; k++) {
    const a = (k / 8) * Math.PI * 2;
    const t = surf.topAt(x + Math.cos(a) * r, z + Math.sin(a) * r);
    if (t === null || Math.abs(t - y) > 0.45 * r) return false;
  }
  return true;
}

/** The spot for a standing part: the anchor when it is clear, else the
 *  nearest clear spot within the search box, else the spot with the most
 *  clearance (only when that beats the anchor's). Without a surface (the
 *  model not in yet) the anchor stands. */
export function clearMount(surf: SurfaceField | null | undefined, q: MountQuery): Mount {
  const ring = q.ring ?? 0.03;
  const need = ring;
  const reach = q.r + ring + 0.6;
  if (!surf) return { x: q.x, z: q.z, clearance: Infinity, moved: false, ok: true };
  const y0 = surf.topAt(q.x, q.z);
  if (y0 === null) return { x: q.x, z: q.z, clearance: Infinity, moved: false, ok: true };
  const accept = q.accept ?? ((): boolean => true);
  const here = clearanceAt(surf, q, q.x, q.z, reach);
  if (here >= need && accept(q.x, q.z)) return { x: q.x, z: q.z, clearance: here, moved: false, ok: true };
  const step = q.step ?? 0.02;
  const dxMax = q.dx ?? 0;
  const dzMax = q.dz ?? 0;
  const maxRise = q.maxRise ?? 0.1;
  let best: { x: number; z: number; c: number; d: number } | null = null;
  let most: { x: number; z: number; c: number; d: number } | null = null;
  const nx = Math.round(dxMax / step);
  const nz = Math.round(dzMax / step);
  for (let i = -nx; i <= nx; i++) {
    for (let j = -nz; j <= nz; j++) {
      const x = q.x + i * step;
      const z = q.z + j * step;
      const d = Math.hypot(i * step, j * step);
      if (!supported(surf, x, z, q.support ?? q.r, y0, maxRise)) continue;
      const c = clearanceAt(surf, q, x, z, reach);
      if (c >= need && (!best || d < best.d - 1e-9) && accept(x, z)) best = { x, z, c, d };
      if (!most || c > most.c + 1e-9 || (Math.abs(c - most.c) <= 1e-9 && d < most.d)) most = { x, z, c, d };
    }
  }
  if (best) return { x: best.x, z: best.z, clearance: best.c, moved: true, ok: true };
  if (most && most.c > here + 0.02) return { x: most.x, z: most.z, clearance: most.c, moved: true, ok: false };
  return { x: q.x, z: q.z, clearance: here, moved: false, ok: false };
}
